<template>
    <div class="graph-card card" :class="{ 'graph-fullscreen': isFullscreen }">
        <!-- Toolbar -->
        <div class="graph-toolbar" v-if="hasGraph">
            <div class="toolbar-left">
                <span class="graph-stats">
                    {{ graphStats.nodes }} nodes &middot; {{ graphStats.edges }} edges
                </span>
            </div>
            <div class="toolbar-right">
                <button class="btn btn-sm btn-light dark-mode-btn" @click="zoomIn" title="Zoom In"><i class="fa-solid fa-magnifying-glass-plus"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="zoomOut" title="Zoom Out"><i class="fa-solid fa-magnifying-glass-minus"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="fitToScreen" title="Fit"><i class="fa-solid fa-down-left-and-up-right-to-center"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="resetView" title="Reset"><i class="fa-solid fa-rotate-left"></i></button>
                <div class="toolbar-divider"></div>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="toggleFullscreen">
                    <i :class="isFullscreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand'"></i>
                </button>
            </div>
        </div>

        <!-- Legend -->
        <div class="graph-legend" v-if="hasGraph">
            <button v-for="item in legendItems" :key="item.type" class="legend-item"
                :class="{ 'legend-item-disabled': hiddenTypes.has(item.type) }"
                @click="toggleType(item.type)">
                <span class="legend-dot" :style="{ backgroundColor: hiddenTypes.has(item.type) ? '#ccc' : item.color }"></span>
                <span class="legend-label">{{ item.label }}</span>
                <span class="legend-count">({{ item.count }})</span>
            </button>
        </div>

        <!-- SVG container -->
        <div ref="graphContainer" class="graph-container"></div>

        <!-- Detail panel -->
        <div class="detail-panel" v-if="detailNode">
            <div class="detail-panel-header">
                <strong>{{ labels[detailNode.type] || detailNode.type }}</strong>
                <button class="btn-close btn-close-white btn-sm" @click="detailNode = null"></button>
            </div>
            <div class="detail-panel-body">
                <div><span class="detail-label">ID:</span> {{ detailNode.id }}</div>
                <div><span class="detail-label">Type:</span> {{ detailNode.type }}</div>
                <div><span class="detail-label">Domain:</span> {{ detailNode.domain }}</div>
                <div><span class="detail-label">Key:</span> {{ detailNode.key }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import * as d3 from "d3";

const NODE_COLORS = {
    prsn: "#3366cc", cookie: "#f39c12", ck: "#f39c12", statid: "#2ecc71",
    ip: "#9b59b6", hh: "#e74c3c", org: "#e74c3c", c_type: "#3366cc", hip: "#9b59b6"
};
const NODE_LABELS = {
    prsn: "Person", cookie: "Cookie", ck: "Cookie", statid: "Stat ID",
    ip: "IP Address", hh: "Household", org: "Organization", c_type: "C Type", hip: "HIP"
};

export default {
    name: "D3ForceGraph",
    props: {
        data: { type: Object, default: null },
        nodeColors: { type: Object, default: () => ({}) },
        nodeLabels: { type: Object, default: () => ({}) }
    },
    emits: ["graph-updated"],
    data() {
        return {
            hasGraph: false,
            isFullscreen: false,
            graphStats: { nodes: 0, edges: 0 },
            legendItems: [],
            hiddenTypes: new Set(),
            detailNode: null,
            simulation: null,
            svg: null,
            zoomBehavior: null,
            graphNodes: [],
            graphEdges: []
        };
    },
    computed: {
        colors() { return { ...NODE_COLORS, ...this.nodeColors }; },
        labels() { return { ...NODE_LABELS, ...this.nodeLabels }; },
        isDark() { return this.$store.state.darkMode; }
    },
    watch: {
        data: {
            handler(val) {
                if (val && val.nodes && val.edges) {
                    this.$nextTick(() => this.loadGraph(val));
                }
            },
            deep: true
        },
        isDark() {
            // Redraw to pick up new link/tooltip colors
            if (this.graphNodes.length > 0) {
                this.$nextTick(() => this.drawGraph());
            }
        }
    },
    mounted() {
        // Clean up any lingering tooltips in the DOM upon mount
        document.querySelectorAll('.graph-tooltip').forEach(el => el.remove());

        if (this.data && this.data.nodes && this.data.edges) {
            this.$nextTick(() => this.loadGraph(this.data));
        }
    },
    beforeUnmount() {
        if (this.simulation) this.simulation.stop();

        // Ensure the tooltip is destroyed when leaving the component
        document.querySelectorAll('.graph-tooltip').forEach(el => el.remove());
    },
    methods: {
        getColor(type) { return this.colors[type] || "#95a5a6"; },

        loadGraph(inputData) {
            this.graphNodes = inputData.nodes.map(n => ({ ...n }));
            this.graphEdges = inputData.edges.map(e => ({
                source: typeof e.source === "object" ? e.source.id : e.source,
                target: typeof e.target === "object" ? e.target.id : e.target
            }));

            this.hasGraph = true;
            this.updateStats();
            this.$nextTick(() => this.drawGraph());
        },

        drawGraph() {
            const container = this.$refs.graphContainer;
            if (!container) return;

            const width = container.clientWidth;
            const height = container.clientHeight || 600;
            const isLarge = this.graphNodes.length > 200;

            d3.select(container).select("svg").remove();

            const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`);

            const g = svg.append("g");

            this.zoomBehavior = d3.zoom()
                .scaleExtent([0.1, 5])
                .on("zoom", (event) => { g.attr("transform", event.transform); });
            svg.call(this.zoomBehavior);

            svg.on("click", () => {
                this.clearHighlight(g);
                this.detailNode = null;
            });

            this.svg = svg;

            const chargeStrength = isLarge ? -120 : -300;
            const linkDist = isLarge ? 40 : 60;

            this.simulation = d3.forceSimulation(this.graphNodes)
                .force("link", d3.forceLink(this.graphEdges).id(d => d.id).distance(linkDist))
                .force("charge", d3.forceManyBody().strength(chargeStrength).theta(isLarge ? 0.9 : 0.8))
                .force("collision", d3.forceCollide().radius(18))
                .force("x", d3.forceX(width / 2).strength(0.08))
                .force("y", d3.forceY(height / 2).strength(0.08))
                .velocityDecay(0.4)
                .alphaDecay(isLarge ? 0.04 : 0.02);

            const link = g.selectAll(".link")
                .data(this.graphEdges)
                .enter()
                .append("line")
                .attr("class", "link")
                .attr("stroke", this.isDark ? "#747474" : "#bdc3c7")
                .attr("stroke-width", 1.5)
                .attr("stroke-opacity", 0.5);

            const node = g.selectAll(".node")
                .data(this.graphNodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0.3).restart();
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0);
                            if (!d.pinned) {
                                d.fx = null;
                                d.fy = null;
                            }
                        })
                );

            node.append("circle")
                .attr("r", 14)
                .attr("fill", d => this.getColor(d.type))
                .attr("stroke", this.isDark ? "#1a1d23" : "#fff")
                .attr("stroke-width", 2);

            node.append("text")
                .attr("text-anchor", "middle")
                .attr("dy", "0.35em")
                .attr("fill", this.isDark ? "#1a1d23" : "#fff")
                .attr("font-size", isLarge ? "7px" : "9px")
                .attr("font-weight", "bold")
                .attr("pointer-events", "none")
                .text(d => (this.labels[d.type] || d.type || "").slice(0, 3).toUpperCase());

            node.on("click", (event, d) => {
                event.stopPropagation();
                this.highlightNeighbors(d, g);
                this.detailNode = d;
            });

            node.on("dblclick", (event, d) => {
                event.stopPropagation();
                d.pinned = !d.pinned;
                if (d.pinned) {
                    d.fx = d.x;
                    d.fy = d.y;
                } else {
                    d.fx = null;
                    d.fy = null;
                }
                d3.select(event.currentTarget).select("circle")
                    .attr("stroke", d.pinned ? "#f39c12" : this.isDark ? "#1a1d23" : "#fff")
                    .attr("stroke-width", d.pinned ? 3 : 2);
            });

            const tooltip = d3.select(container).select(".graph-tooltip");
            if (tooltip.empty()) {
                d3.select(container).append("div")
                    .attr("class", "graph-tooltip")
                    .style("opacity", 0)
                    .style("position", "absolute")
                    .style("pointer-events", "none"); 
            }

            node.on("mouseover", (event, d) => {
                const tip = container.querySelector(".graph-tooltip");
                if (tip) {
                    const conns = this.graphEdges.filter(e => {
                        const s = typeof e.source === "object" ? e.source.id : e.source;
                        const t = typeof e.target === "object" ? e.target.id : e.target;
                        return s === d.id || t === d.id;
                    }).length;
                    tip.innerHTML = `<strong>${this.labels[d.type] || d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}<br>Connections: ${conns}`;
                    tip.style.opacity = "1";
                    const rect = container.getBoundingClientRect();
                    tip.style.left = (event.clientX - rect.left + 15) + "px";
                    tip.style.top = (event.clientY - rect.top - 10) + "px";
                }
            });

            node.on("mouseout", () => {
                const tip = container.querySelector(".graph-tooltip");
                if (tip) tip.style.opacity = "0";
            });

            let tickCount = 0;
            const throttle = isLarge ? 3 : 1;

            this.simulation.on("tick", () => {
                tickCount++;
                if (tickCount % throttle !== 0) return;

                link
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });

            this.simulation.on("end", () => {
                this.fitToScreen();
            });
        },

        highlightNeighbors(d, g) {
            const neighborIds = new Set();
            neighborIds.add(d.id);
            this.graphEdges.forEach(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                if (s === d.id) neighborIds.add(t);
                if (t === d.id) neighborIds.add(s);
            });

            g.selectAll(".node").style("opacity", n => neighborIds.has(n.id) ? 1 : 0.12);
            g.selectAll(".link").style("opacity", e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return (s === d.id || t === d.id) ? 0.9 : 0.05;
            }).style("stroke", e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return (s === d.id || t === d.id) ? "#3498db" : "#bdc3c7";
            }).style("stroke-width", e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return (s === d.id || t === d.id) ? 2.5 : 1.5;
            });
        },

        clearHighlight(g) {
            if (!g) {
                if (!this.svg) return;
                g = this.svg.select("g");
            }
            g.selectAll(".node").style("opacity", 1);
            g.selectAll(".link")
                .style("opacity", 0.5)
                .style("stroke", this.isDark ? "#747474" : "#bdc3c7")
                .style("stroke-width", 1.5);
        },

        toggleType(type) {
            if (this.hiddenTypes.has(type)) this.hiddenTypes.delete(type);
            else this.hiddenTypes.add(type);
            this.applyTypeFilter();
        },

        applyTypeFilter() {
            if (!this.svg) return;
            const g = this.svg.select("g");
            g.selectAll(".node").style("display", d => this.hiddenTypes.has(d.type) ? "none" : null);
            g.selectAll(".link").style("display", e => {
                const s = typeof e.source === "object" ? e.source : { type: "" };
                const t = typeof e.target === "object" ? e.target : { type: "" };
                return (this.hiddenTypes.has(s.type) || this.hiddenTypes.has(t.type)) ? "none" : null;
            });
        },

        zoomIn() {
            if (this.svg && this.zoomBehavior) {
                this.svg.transition().duration(300).call(this.zoomBehavior.scaleBy, 1.3);
            }
        },

        zoomOut() {
            if (this.svg && this.zoomBehavior) {
                this.svg.transition().duration(300).call(this.zoomBehavior.scaleBy, 0.7);
            }
        },

        fitToScreen() {
            if (!this.svg || !this.zoomBehavior || !this.$refs.graphContainer) return;
            const container = this.$refs.graphContainer;
            const width = container.clientWidth;
            const height = container.clientHeight || 600;

            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            this.graphNodes.forEach(n => {
                if (n.x !== undefined && n.y !== undefined) {
                    minX = Math.min(minX, n.x);
                    maxX = Math.max(maxX, n.x);
                    minY = Math.min(minY, n.y);
                    maxY = Math.max(maxY, n.y);
                }
            });

            if (!isFinite(minX)) return;

            const pad = 60;
            const gWidth = (maxX - minX) || 1;
            const gHeight = (maxY - minY) || 1;
            const scale = Math.min(width / (gWidth + 2 * pad), height / (gHeight + 2 * pad), 2);
            const tx = width / 2 - scale * (minX + gWidth / 2);
            const ty = height / 2 - scale * (minY + gHeight / 2);

            this.svg.transition().duration(500).call(
                this.zoomBehavior.transform,
                d3.zoomIdentity.translate(tx, ty).scale(scale)
            );
        },

        resetView() {
            this.clearHighlight();
            this.hiddenTypes = new Set();
            this.applyTypeFilter();
            this.detailNode = null;
            this.fitToScreen();
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
            this.$nextTick(() => {
                if (this.svg) {
                    const container = this.$refs.graphContainer;
                    const w = container.clientWidth;
                    const h = container.clientHeight || 600;
                    this.svg.attr("viewBox", `0 0 ${w} ${h}`);
                }
                this.fitToScreen();
            });
        },

        updateStats() {
            this.graphStats = { nodes: this.graphNodes.length, edges: this.graphEdges.length };
            const tc = {};
            this.graphNodes.forEach(n => { const t = n.type; tc[t] = (tc[t] || 0) + 1; });
            this.legendItems = Object.entries(tc).sort((a, b) => b[1] - a[1])
                .map(([type, count]) => ({ type, label: this.labels[type] || type, color: this.getColor(type), count }));
        }
    }
};
</script>

<style scoped>
.graph-card { position: relative; }
.graph-fullscreen {
    position: fixed !important; top: 0; left: 0; right: 0; bottom: 0;
    z-index: 1050; margin: 0 !important; border-radius: 0 !important;
    display: flex; flex-direction: column;
}
.graph-fullscreen .graph-container { min-height: unset; flex: 1; }

.graph-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 6px 12px; border-bottom: 1px solid #e3ebf6; background: #f9fbfd;
    flex-wrap: wrap; gap: 6px;
}
.toolbar-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.toolbar-right { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }

.toolbar-btn {
    background: none; border: 1px solid #d2ddec; border-radius: 4px;
    padding: 4px 8px; cursor: pointer; color: #6e84a3; font-size: 14px; transition: all 0.15s;
}
.toolbar-btn:hover { background: #e3ebf6; color: #12263f; }
.toolbar-divider { width: 1px; height: 20px; background: #d2ddec; margin: 0 4px; }
.graph-stats { font-size: 12px; color: #6e84a3; font-weight: 500; white-space: nowrap; }

.graph-legend {
    display: flex; flex-wrap: wrap; gap: 4px; align-items: center;
    padding: 6px 12px; border-bottom: 1px solid #e3ebf6; background: #f9fbfd;
}
.legend-item {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border: 1px solid #d2ddec; border-radius: 12px;
    background: white; cursor: pointer; font-size: 11px; transition: all 0.15s;
}
.legend-item:hover { border-color: #95aac9; }
.legend-item-disabled { opacity: 0.5; background: #f5f5f5; }
.legend-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.legend-label { color: #12263f; font-weight: 500; }
.legend-count { color: #95aac9; }

.graph-container {
    position: relative; flex: 1; width: 100%; min-height: 600px;
    border: none; background-color: #fff; outline: none; cursor: grab;
}

.detail-panel {
    position: absolute; top: 80px; right: 12px;
    width: 260px; background: white; border: 1px solid #d2ddec;
    border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    z-index: 30; overflow: hidden;
}
.detail-panel-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; background: #12263f; color: white; font-size: 13px;
}
.detail-panel-body { padding: 12px 14px; font-size: 12px; line-height: 1.8; }
.detail-label { color: #6e84a3; font-weight: 500; margin-right: 4px; }

.graph-tooltip {
    position: absolute; background-color: rgba(18, 38, 63, 0.92); color: #fff;
    padding: 10px 14px; border-radius: 6px; font-size: 12px;
    pointer-events: none; z-index: 20; line-height: 1.6;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-width: 280px;
    opacity: 0; transition: opacity 0.15s;
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {

    /* Toolbar */
    .graph-toolbar {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }

    .graph-stats {
        color: $dark-text-primary;
    }

    .toolbar-divider {
        background: #2d3748;
    }

    .dark-mode-btn {
        background: $dark-body-bg;
    }
    .dark-mode-btn:hover {
        background: $dark-border-color;
    }

    /* Legend */
    .graph-legend {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }

    .legend-item {
        background: #253344;
        border-color: #2d3748;
        color: #e2e8f0;
    }

    .legend-item:hover {
        border-color: #4a6080;
    }

    .legend-item-disabled {
        background: #1a2433;
        opacity: 0.4;
    }

    .legend-label {
        color: #e2e8f0;
    }

    .legend-count {
        color: #8899aa;
    }

    /* Graph container */
    .graph-container {
        background-color: $dark-card-bg;
    }

    /* Detail panel */
    .detail-panel {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }

    .detail-panel-header {
        background: $dark-border-color;
    }

    .detail-panel-body {
        color: $dark-text-primary;
    }

    .detail-label {
        color: $dark-text-muted;
    }

    /* Tooltip */
    .graph-tooltip {
        background-color: rgba(13, 27, 42, 0.97);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
}
</style>
